import React from 'react';
import { Select, Typography, Tooltip } from 'antd';

const { Text } = Typography;

/**
 * TokenSpacingControl - Chrome DevTools-style box model visualization
 * Shows nested boxes for margin, border, padding, and content
 */
const TokenSpacingControl = ({
    value = {},
    onChange,
    tokens = [],
    label = 'Spacing',
    type = 'padding', // 'padding' | 'margin' | 'gap'
    // Additional values for full box model display
    marginValue = {},
    paddingValue = {},
    onMarginChange,
    onPaddingChange
}) => {
    // Convert spacing tokens to select options
    const tokenOptions = [
        { value: '0', label: '0', px: 0 },
        ...tokens.map(t => ({
            value: t.name,
            label: `${t.name} (${t.values?.mobile || 0}px)`,
            px: t.values?.mobile || 0
        }))
    ];

    const handleChange = (direction, tokenValue) => {
        onChange?.({
            ...value,
            [direction]: tokenValue
        });
    };

    // Get pixel value for display
    const getPxValue = (tokenName) => {
        if (!tokenName || tokenName === '0') return 0;
        const token = tokens.find(t => t.name === tokenName);
        return token?.values?.mobile || parseInt(tokenName, 10) || 0;
    };

    // Single value mode (for gap)
    if (type === 'gap') {
        return (
            <div style={{ marginBottom: 16 }}>
                <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 8 }}>
                    {label.toUpperCase()}
                </Text>
                <Select
                    size="small"
                    value={value.all || '0'}
                    onChange={(v) => onChange?.({ all: v })}
                    options={tokenOptions}
                    style={{ width: '100%' }}
                    dropdownStyle={{ background: '#161b22' }}
                />
            </div>
        );
    }

    // Box model colors (muted, like Chrome DevTools)
    const colors = {
        margin: { bg: 'rgba(194, 161, 120, 0.4)', border: 'rgba(194, 161, 120, 0.6)' },
        border: { bg: 'rgba(219, 195, 142, 0.4)', border: 'rgba(219, 195, 142, 0.6)' },
        padding: { bg: 'rgba(147, 176, 143, 0.4)', border: 'rgba(147, 176, 143, 0.6)' },
        content: { bg: 'rgba(139, 178, 199, 0.5)', border: 'rgba(139, 178, 199, 0.7)' }
    };

    return (
        <div style={{ marginBottom: 16 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, display: 'block', marginBottom: 12 }}>
                {label.toUpperCase()}
            </Text>

            {/* Chrome DevTools Box Model */}
            <div style={{
                background: '#1e1e1e',
                borderRadius: 8,
                padding: 16,
                fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
                {/* Margin Layer */}
                <div style={{
                    background: colors.margin.bg,
                    border: `1px solid ${colors.margin.border}`,
                    padding: 8,
                    position: 'relative'
                }}>
                    <span style={{
                        position: 'absolute',
                        top: 4,
                        left: 8,
                        fontSize: 10,
                        color: '#c9a876'
                    }}>
                        margin
                    </span>

                    {/* Margin values */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: '#e6edf3',
                        marginTop: 2
                    }}>
                        {type === 'margin' ? getPxValue(value.top) : 0}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
                        <div style={{
                            width: 24,
                            textAlign: 'center',
                            fontSize: 11,
                            color: '#e6edf3'
                        }}>
                            {type === 'margin' ? getPxValue(value.left) : 0}
                        </div>

                        {/* Border Layer */}
                        <div style={{
                            flex: 1,
                            background: colors.border.bg,
                            border: `1px dashed ${colors.border.border}`,
                            padding: 8
                        }}>
                            <span style={{
                                fontSize: 10,
                                color: '#d4b85c',
                                display: 'block',
                                marginBottom: 4
                            }}>
                                border
                            </span>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                fontSize: 11,
                                color: '#e6edf3'
                            }}>
                                0
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                                <div style={{
                                    width: 20,
                                    textAlign: 'center',
                                    fontSize: 11,
                                    color: '#e6edf3'
                                }}>
                                    0
                                </div>

                                {/* Padding Layer */}
                                <div style={{
                                    flex: 1,
                                    background: colors.padding.bg,
                                    border: `1px solid ${colors.padding.border}`,
                                    padding: 8
                                }}>
                                    <span style={{
                                        fontSize: 10,
                                        color: '#8eb485',
                                        display: 'block',
                                        marginBottom: 4
                                    }}>
                                        padding
                                    </span>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        color: '#e6edf3'
                                    }}>
                                        {type === 'padding' ? getPxValue(value.top) : 0}
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                                        <div style={{
                                            width: 16,
                                            textAlign: 'center',
                                            fontSize: 11,
                                            color: '#e6edf3'
                                        }}>
                                            {type === 'padding' ? getPxValue(value.left) : 0}
                                        </div>

                                        {/* Content */}
                                        <div style={{
                                            flex: 1,
                                            background: colors.content.bg,
                                            border: `1px solid ${colors.content.border}`,
                                            padding: '8px 12px',
                                            textAlign: 'center',
                                            fontSize: 11,
                                            color: '#e6edf3'
                                        }}>
                                            content
                                        </div>

                                        <div style={{
                                            width: 16,
                                            textAlign: 'center',
                                            fontSize: 11,
                                            color: '#e6edf3'
                                        }}>
                                            {type === 'padding' ? getPxValue(value.right) : 0}
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        fontSize: 11,
                                        color: '#e6edf3',
                                        marginTop: 4
                                    }}>
                                        {type === 'padding' ? getPxValue(value.bottom) : 0}
                                    </div>
                                </div>

                                <div style={{
                                    width: 20,
                                    textAlign: 'center',
                                    fontSize: 11,
                                    color: '#e6edf3'
                                }}>
                                    0
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                fontSize: 11,
                                color: '#e6edf3',
                                marginTop: 4
                            }}>
                                0
                            </div>
                        </div>

                        <div style={{
                            width: 24,
                            textAlign: 'center',
                            fontSize: 11,
                            color: '#e6edf3'
                        }}>
                            {type === 'margin' ? getPxValue(value.right) : 0}
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontSize: 11,
                        color: '#e6edf3',
                        marginTop: 8
                    }}>
                        {type === 'margin' ? getPxValue(value.bottom) : 0}
                    </div>
                </div>
            </div>

            {/* Spacing selectors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
                <div>
                    <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Top</Text>
                    <Select
                        size="small"
                        value={value.top || '0'}
                        onChange={(v) => handleChange('top', v)}
                        options={tokenOptions}
                        style={{ width: '100%' }}
                    />
                </div>
                <div>
                    <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Right</Text>
                    <Select
                        size="small"
                        value={value.right || '0'}
                        onChange={(v) => handleChange('right', v)}
                        options={tokenOptions}
                        style={{ width: '100%' }}
                    />
                </div>
                <div>
                    <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Bottom</Text>
                    <Select
                        size="small"
                        value={value.bottom || '0'}
                        onChange={(v) => handleChange('bottom', v)}
                        options={tokenOptions}
                        style={{ width: '100%' }}
                    />
                </div>
                <div>
                    <Text style={{ color: '#484f58', fontSize: 10, display: 'block', marginBottom: 4 }}>Left</Text>
                    <Select
                        size="small"
                        value={value.left || '0'}
                        onChange={(v) => handleChange('left', v)}
                        options={tokenOptions}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>
        </div>
    );
};

export default TokenSpacingControl;
